import type { PayoutSetupCopyKey } from './payoutSetupCopy';

/** The fields of GET /locations/:id/connect/status the bank card decides on. Every one is optional: an older Api sends none of
 *  the payee fields, and the card then reads exactly as it did. */
export interface LocationBankStatusFields {
  has_account?: boolean; ready?: boolean; status?: string;
  linked_share_payee?: boolean; can_open_onboarding?: boolean;
  site_host_electricity?: { ready: boolean; status: string } | null;
}

export interface LocationBankCardView {
  /** The heading key, or null for the shared "Location bank account" heading. */
  heading: PayoutSetupCopyKey | null;
  line: PayoutSetupCopyKey;
  note: PayoutSetupCopyKey | null;
  /** Show the country, authority and Stripe button. False only when the Api says it refuses onboarding on this account. */
  setup: boolean;
  /** The site host electricity payee's line, or null when the card has no such section. */
  siteHost: PayoutSetupCopyKey | null;
  siteHostReady: boolean;
}

/**
 * What the location bank card says (Warren, 29 October 2026, The Novelle). The per-location fact is the split itself: only a
 * location that has its own site host electricity payee (Location.site_host_electricity_account_id, recorded by
 * create-site-host-account.ts because its linked payee is NOT the site host) names its linked account the charger owner's
 * legacy share payee and shows the site host's electricity bank beside it, from the Api's live read. Every other linked
 * location (a hotel that bought its own chargers, a town paid on its own company account) keeps the location bank heading and
 * line it always had, because that account is the site host's own; it offers no setup when the Api refuses it, and says why
 * in words that name nobody.
 */
export function locationBankCardView(s: LocationBankStatusFields | null | undefined): LocationBankCardView {
  const linked = s?.linked_share_payee === true, host = s?.site_host_electricity ?? null, split = linked && host !== null;
  const setup = s?.can_open_onboarding !== false;
  const line: PayoutSetupCopyKey = split ? (s?.ready ? 'sharePayeeReady' : 'sharePayeeAttention')
    : s?.ready ? 'locationReady' : s?.status === 'pending' ? 'locationPending' : s?.has_account ? 'locationAttention' : 'locationNoAccount';
  const siteHost: PayoutSetupCopyKey | null = !host ? null : host.ready ? 'siteHostReady'
    : host.status === 'unavailable' ? 'siteHostUnavailable' : 'siteHostPending';
  return { heading: split ? 'sharePayeeHeading' : null, line,
    note: split ? 'sharePayeeNote' : !setup ? 'linkedPayeeNote' : null,
    setup, siteHost, siteHostReady: host?.ready === true };
}
